import React, { useEffect, useRef, useState } from 'react';
import { useToolbar } from '@ohif/core/src';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icons,
  ToolButton,
  ToolButtonList,
  ToolButtonListDefault,
  ToolButtonListItem,
} from '@ohif/ui-next';

/** Keep tool choices inside the clinical image workspace, away from exam lists. */
export default function ClinicalToolButtonList({
  buttonSection,
  id,
}: {
  buttonSection: string;
  id: string;
}) {
  const { onInteraction, toolbarButtons } = useToolbar({ buttonSection });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openedScrollPosition = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [boundary, setBoundary] = useState<Element | null>(null);
  const [sideOffset, setSideOffset] = useState(4);

  useEffect(() => {
    const scrollArea = triggerRef.current?.closest(
      '.toolbar-fixed-ends__scroller, [data-radix-scroll-area-viewport]'
    );
    const close = () => {
      // Focusing an offscreen trigger can queue a scroll event before opening.
      // Only dismiss when its position actually changes after the menu opens.
      if (
        openedScrollPosition.current !== null &&
        scrollArea?.scrollLeft !== openedScrollPosition.current
      ) {
        setOpen(false);
        openedScrollPosition.current = null;
      }
    };
    scrollArea?.addEventListener('scroll', close);
    return () => scrollArea?.removeEventListener('scroll', close);
  }, [toolbarButtons?.length]);

  if (!toolbarButtons?.length) return null;
  const primary =
    toolbarButtons.find(button => button.componentProps.isActive)?.componentProps ||
    toolbarButtons[0].componentProps;

  return (
    <ToolButtonList>
      <ToolButtonListDefault>
        <div
          data-cy={`${id}-split-button-primary`}
          data-tool={primary.id}
          data-active={primary.isActive}
          className="flex min-h-[48px] flex-col items-center justify-center py-0"
        >
          <ToolButton
            {...primary}
            onInteraction={({ itemId }) =>
              onInteraction?.({ id, itemId, commands: primary.commands })
            }
            className="flex !h-auto !w-auto flex-col items-center justify-center gap-1 px-2"
          >
            <Icons.ByName name={primary.icon || 'MissingIcon'} />
            <span className="whitespace-nowrap text-center text-xs leading-none">
              {primary.label || primary.tooltip}
            </span>
          </ToolButton>
        </div>
      </ToolButtonListDefault>
      <div data-cy={`${id}-split-button-secondary`}>
        <DropdownMenu
          modal={false}
          open={open}
          onOpenChange={next => {
            const scrollArea = triggerRef.current?.closest(
              '.toolbar-fixed-ends__scroller, [data-radix-scroll-area-viewport]'
            );
            openedScrollPosition.current = next ? (scrollArea?.scrollLeft ?? 0) : null;
            const workspace = triggerRef.current?.closest('[data-cy="clinical-imaging"]');
            const imageArea = workspace?.querySelector('[data-cy="viewport-grid"]');
            setBoundary(imageArea || workspace || null);
            // The toolbar has bottom padding: anchor below the image area's top,
            // rather than immediately below the shorter trigger button.
            if (next && imageArea && triggerRef.current) {
              setSideOffset(
                Math.max(
                  4,
                  imageArea.getBoundingClientRect().top +
                    8 -
                    triggerRef.current.getBoundingClientRect().bottom
                )
              );
            }
            setOpen(next);
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              ref={triggerRef}
              variant="ghost"
              size="icon"
              aria-label={`${primary.label || '工具'}：更多工具`}
            >
              <Icons.ByName
                name="chevron-down"
                className="text-primary h-4 w-4"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            data-cy="clinical-tool-menu"
            className="clinical-tool-menu"
            side="bottom"
            align="start"
            sideOffset={sideOffset}
            collisionBoundary={boundary}
            collisionPadding={8}
            sticky="always"
            style={{
              width: 'min(268px, var(--radix-dropdown-menu-content-available-width))',
              minWidth: 0,
              maxHeight: 'min(360px, var(--radix-dropdown-menu-content-available-height))',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <div className="grid grid-cols-3 gap-1">
              {toolbarButtons.map(({ componentProps: item }) => (
                <ToolButtonListItem
                  key={item.id}
                  icon={item.icon}
                  data-cy={item.id}
                  data-tool={item.id}
                  data-active={item.isActive}
                  disabled={item.disabled}
                  disabledText={item.disabledText}
                  tooltip={item.tooltip}
                  onSelect={() => onInteraction?.({ id, itemId: item.id, commands: item.commands })}
                  className="!m-0 !flex min-h-[60px] min-w-0 !flex-col !items-center !justify-center !space-x-0 !space-y-1 px-1"
                >
                  <span className="text-center text-xs leading-tight">
                    {item.label || item.tooltip || item.id}
                  </span>
                </ToolButtonListItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </ToolButtonList>
  );
}
